import React, { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form as AntdForm } from 'antd';
import { routes } from '../../../../../utils/helpers/ApiClient';
import AppBarCMS from '../../../../basic/Navigation/AppBarCMS/AppBarCMS';
import PhdPageContext from '../../../../common/Phd/PhdPageContext.jsx';
import { AdminContentBlock, Spinner } from '../../../../common';
import Form from '../../../../antd/form/Form/Form';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';
import { applyPhdValidationErrors } from '../utils/phdApiFeedback.js';
import useIsUserPhdAdmin from '../../../../../hooks/useIsUserPhdAdmin.jsx';
import {
  useGetPhdStudentQuery,
  useSavePhdStudentMutation,
} from '../../../../../redux/services/phd';
import { usePhdReferenceData } from '../usePhdReferenceData.jsx';
import PhdStudentsEditForm from './PhdStudentsEditForm.jsx';
import { usePhdDirtyLeaveGuard } from '../usePhdDirtyLeaveGuard.jsx';
import checkPermission from '../../../../../utils/helpers/checkPermission.jsx';
import PhdStudentLifecycleSection from './PhdStudentLifecycleSection.jsx';
import PhdArchivedRecordNotice from '../PhdArchivedRecordNotice.jsx';

const toDayjs = (value) => (value ? dayjs(value) : null);

const PhdStudentsEdit = ({ mode }) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = AntdForm.useForm();
  const user = useSelector((state) => state.auth.user);
  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const pageTitle = isCreate
    ? t('Pridanie doktoranda')
    : t('Editácia doktoranda');

  useEffect(() => {
    document.title = `${pageTitle} - FRI CMS`;
  }, [pageTitle]);

  const {
    data: detail,
    isLoading,
    refetch,
  } = useGetPhdStudentQuery(id, {
    skip: !isEdit,
  });
  const [savePhdStudent, { isLoading: isSaving }] = useSavePhdStudentMutation();
  const { saveSuccess, saveFailed } = useToastMessages();
  const isPhdAdmin = useIsUserPhdAdmin();

  const {
    personOptions,
    departmentOptions,
    studyFieldOptions,
    isLoading: isReferenceLoading,
  } = usePhdReferenceData({ includeStudents: false });

  const initials = useMemo(() => {
    if (isCreate) {
      return {
        person_id: undefined,
        department_id: undefined,
        phd_study_field_id: undefined,
        study_form: undefined,
        standard_duration_months: undefined,
        state: undefined,
        date_study_start: null,
        birthplace: undefined,
        birth_date: null,
        phone: undefined,
        application_submission_year: undefined,
        dissertation_exam_result: undefined,
        dissertation_exam_transcript: undefined,
        date_dissertation_exam: null,
        address_street: undefined,
        address_house_number: undefined,
        address_city: undefined,
        address_postal_code: undefined,
        address_country: undefined,
      };
    }

    if (!detail) {
      return null;
    }

    const primaryAddress =
      [...(detail.addresses || [])].sort(
        (left, right) => Number(left.id) - Number(right.id),
      )[0] || {};

    return {
      ...detail,
      birth_date: toDayjs(detail.birth_date),
      date_study_start: toDayjs(detail.date_study_start),
      standard_duration_months: detail.standard_duration_months,
      date_dissertation_exam: toDayjs(
        detail.dissertation_exam_milestone?.date_dissertation_exam,
      ),
      dissertation_exam_result:
        detail.dissertation_exam_milestone?.dissertation_exam_result,
      dissertation_exam_transcript:
        detail.dissertation_exam_milestone?.dissertation_exam_transcript,
      address_street: primaryAddress.street,
      address_house_number: primaryAddress.house_number,
      address_city: primaryAddress.city,
      address_postal_code: primaryAddress.postal_code,
      address_country: primaryAddress.country,
    };
  }, [detail, isCreate]);
  const isFormMounted =
    !isReferenceLoading && Boolean(initials) && (!isEdit || !isLoading);
  const { modal, onSaveFailed, onSaveSuccess, onValuesChange } =
    usePhdDirtyLeaveGuard(form, initials, isFormMounted);

  const mergedPersonOptions = useMemo(() => {
    const options = [...(personOptions || [])];

    if (
      isEdit &&
      detail?.person_id &&
      !options.some((option) => option.value === detail.person_id)
    ) {
      options.unshift({
        value: detail.person_id,
        label:
          detail.person?.full_name ||
          detail.person?.mail ||
          `${detail.person_id}`,
      });
    }

    return options;
  }, [detail, isEdit, personOptions]);

  const handleSubmit = async (values) => {
    const payload = {
      ...values,
      birth_date: values.birth_date
        ? values.birth_date.format('YYYY-MM-DD')
        : null,
      date_study_start: values.date_study_start
        ? values.date_study_start.format('YYYY-MM-DD')
        : null,
      date_dissertation_exam: values.date_dissertation_exam
        ? values.date_dissertation_exam.format('YYYY-MM-DD')
        : null,
    };

    try {
      await savePhdStudent({
        id: isEdit ? detail.id : undefined,
        body: payload,
      }).unwrap();
      saveSuccess();
      onSaveSuccess(() => navigate(routes.cmsPhdStudents));
    } catch (error) {
      onSaveFailed();
      applyPhdValidationErrors(form, error);
      saveFailed(error);
    }
  };

  if ((isEdit && isLoading) || isReferenceLoading || !initials) {
    return <Spinner />;
  }

  const isLifecycleReadOnly = detail?.is_read_only === true;
  const canManageInterruptions =
    !isLifecycleReadOnly &&
    isPhdAdmin &&
    checkPermission(user, 'phd-interruptions.manage');
  const canAdjustDeadlines =
    !isLifecycleReadOnly &&
    isPhdAdmin &&
    checkPermission(user, 'phd-deadlines.adjust');
  const handleLifecycleChanged = async () => {
    const refreshed = await refetch();
    if (refreshed.data?.state) {
      form.setFieldValue('state', refreshed.data.state);
    }
  };

  return (
    <>
      {!isLifecycleReadOnly && modal}
      <AppBarCMS
        title={pageTitle}
        backUrl={routes.cmsPhdStudents}
        backUrlTooltip={t('Doktorandi')}
        permissionsBasic="phd-students"
      />
      <div style={{ margin: '10px' }}>
        <PhdPageContext
          title={t('Ako funguje profil doktoranda?')}
          description={t(
            'Administrátor doktorandského štúdia nastaví študijný program, formu a obdobie štúdia. Uložený úplný profil možno aktivovať; ďalšie kroky potom tvoria téma, plán a ročné povinnosti.',
          )}
        />
        <AdminContentBlock
          icon="fa fa-user"
          title={pageTitle}
          link={routes.cmsPhdStudents}
        >
          <PhdArchivedRecordNotice record={detail} />
          <Form
            form={form}
            initialValues={initials}
            onFinish={handleSubmit}
            onValuesChange={onValuesChange}
            disabled={isLifecycleReadOnly}
          >
            <PhdStudentsEditForm
              personOptions={mergedPersonOptions}
              departmentOptions={departmentOptions}
              studyFieldOptions={studyFieldOptions}
              isSubmitting={isSaving}
              isPhdAdmin={isPhdAdmin}
              isReadOnly={isLifecycleReadOnly}
            />
          </Form>
          {isEdit ? (
            <PhdStudentLifecycleSection
              student={detail}
              canManageInterruptions={canManageInterruptions}
              canAdjustDeadlines={canAdjustDeadlines}
              onLifecycleChanged={handleLifecycleChanged}
            />
          ) : null}
        </AdminContentBlock>
      </div>
    </>
  );
};

export default PhdStudentsEdit;
